(ns hai.library.sources.filepicker
  "Generic filepicker based on the [[hai.storage.humble]] generalized storage
  layer. Putting it here for now as it's used
  by [[hai.library.sources.sharepoint-picker]], but it's not really
  hai.sources.library specific, and could be moved somewhere more general."
  (:require
   [hai.ui.components.button :as button]
   [hai.ui.components.link :as link]
   [hai.ui.components.spinners :as spinners]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [lambdaisland.deja-fu :as fu]
   [lambdaisland.ornament :as o]
   [reagent.core :as reagent]))

(def dir-mime-type "application/vnd.humble.directory")

(def mime-type->icon
  {;; --- Internal representation for folders and drives ---
   dir-mime-type icons/folder

   ;; --- PDF ---
   "application/pdf"                                         icons/outlined-picture_as_pdf

   ;; --- Office Documents ---
   "application/msword"                                      icons/outlined-description
   "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
   icons/outlined-description
   "application/vnd.ms-excel"                                icons/outlined-insert_chart
   "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
   icons/outlined-insert_chart
   "application/vnd.ms-powerpoint"                           icons/outlined-slideshow
   "application/vnd.openxmlformats-officedocument.presentationml.presentation"
   icons/outlined-slideshow

   ;; --- Images ---
   "image/jpeg"                                              icons/outlined-image
   "image/png"                                               icons/outlined-image
   "image/gif"                                               icons/outlined-image
   "image/webp"                                              icons/outlined-image
   "image/svg+xml"                                           icons/outlined-image

   ;; --- Text and Data ---
   "text/plain"                                              icons/outlined-text_snippet
   "text/csv"                                                icons/outlined-grid_on
   "application/json"                                        icons/outlined-data_object
   "application/xml"                                         icons/outlined-terminal
   "text/xml"                                                icons/outlined-terminal

   ;; --- Archives ---
   "application/zip"                                         icons/outlined-folder_zip
   "application/x-tar"                                       icons/outlined-folder_zip
   "application/x-rar-compressed"                            icons/outlined-folder_zip

   ;; --- Media ---
   "video/mp4"                                               icons/outlined-ondemand_video
   "video/quicktime"                                         icons/outlined-ondemand_video
   "video/webm"                                              icons/outlined-ondemand_video
   "audio/mpeg"                                              icons/outlined-library_music
   "audio/wav"                                               icons/outlined-library_music
   "audio/ogg"                                               icons/outlined-library_music

   ;; --- Web/Code ---
   "text/html"                                               icons/outlined-web
   "application/xhtml+xml"                                   icons/outlined-web
   "text/css"                                                icons/outlined-code
   "text/javascript"                                         icons/outlined-code

   ;; --- Fallback/Unknown ---
   "application/octet-stream"                                icons/outlined-insert_drive_file})

(defn prep-state [conn-id url]
  (let [state (reagent/atom {:conn-id conn-id
                             :path []})]
    (add-watch state ::fetch (fn [k r o {:keys [url conn-id]}]
                               (>e [:fetch {:url "/storage/v1/info"
                                            :query-params {:humble-url url
                                                           :conn-id conn-id}
                                            :request-id (doto [::storage-info conn-id url] prn)
                                            :store-in [::storage-info conn-id url]}])))
    (swap! state assoc :url url)
    state))

(defn drill-down [!state url]
  (swap! !state (fn [state]
                  (-> state
                      (update :path conj (:url state))
                      (assoc :url url)))))

(defn nav-up [!state on-nav-out]
  (let [parent (peek (:path @!state))]
    (if parent
      (swap! !state (fn [state]
                      (-> state
                          (update :path pop)
                          (assoc :url (peek (:path state))))))
      (on-nav-out))))

(o/defstyled entry-pane-header :div
  :flex :items-center :gap-2
  [:h3 :flex-grow]
  ([!state entry {:keys [on-confirm on-nav-out confirm-button-text]
                  :as opts}]
   [:<>
    [button/borderless-button {:on-click #(nav-up !state on-nav-out)} [icons/round-arrow_back_ios]]
    [:h3 {:data-humble-url (:storage.entry/self entry)} (:storage.entry/display-name entry)]
    [button/action-button {:on-click #(on-confirm entry)} confirm-button-text]]))

(o/defstyled entry-list-item :div
  [button/borderless-button
   :flex :items-center :text-lg :gap-2]
  [:.display-name :flex-grow]
  ([!state {:storage.entry/keys [display-name self mime-type] :as entry}]
   [:<>
    [button/borderless-button
     {:on-click #(drill-down !state self)}
     [(get mime-type->icon mime-type icons/outlined-insert_drive_file)
      {:style {:width "32px"
               :height "32px"}}]
     [:span.display-name
      display-name]]]))

(o/defstyled entry-info :ul
  :flex :flex-col :gap-2
  ([{:storage.entry/keys [display-name web-url type-desc size
                          created last-modified modified created-by]}]
   [:<>
    (when web-url
      [link/link {:href web-url :target "_blank"} "Open in new tab"])
    (when size
      [:li [:strong "Size:"] " " size " bytes"])
    (when type-desc
      [:li [:strong "Type:"] " " type-desc])
    (when created
      [:li [:strong "Created:"] " " (fu/format created)])
    (when last-modified
      [:li [:strong "Last modified:"] " " (fu/format last-modified)])
    (when-let [name (-> created-by :user/display-name)]
      [:li [:strong "Created by:"] " " name])]))

(o/defstyled entry-pane :div
  :flex :flex-col :gap-4
  [:hr :border-gray-300]
  ([!state entry opts]
   [:<>
    [entry-pane-header !state entry opts]
    [:hr]
    (if (= dir-mime-type (:storage.entry/mime-type entry))
      (if-let [children (seq (:storage.entry/children entry))]
        (into
         [:<>]
         (for [entry children]
           [entry-list-item !state entry]))
        [:<> "No entries"])
      [entry-info entry])]))

(o/defstyled error-pane entry-pane
  ([!state message {:keys [on-nav-out]}]
   [:<>
    [:div {:class (str entry-pane-header)}
     [button/borderless-button {:on-click #(nav-up !state on-nav-out)} [icons/round-arrow_back_ios]]
     [:h3 "Failed to load"]]
    [:hr]
    message]))

(defn filepicker [{:keys [conn-id url on-confirm on-nav-out confirm-button-text]
                   :or {confirm-button-text "Confirm"}
                   :as opts}]
  (reagent/with-let [!state (prep-state conn-id url)]
    (let [req-id [::storage-info conn-id (:url @!state)]
          state @(<s [:fetch/state req-id])
          res   @(<s [:fetch/body req-id])]
      [:<>
       (cond
         (= :error state)
         [error-pane !state (:error (:body @(<s [:fetch/error req-id]))) opts]

         res
         [entry-pane !state res opts]

         (= state :start)
         [spinners/pulse-dots]

         :else
         (pr-str state))])))
